import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './ui.tsx';

/**
 * A right-click / kebab menu.
 *
 * Offered both ways deliberately. Right-click is what a Windows user reaches
 * for out of habit, and a visible ⋯ button is what makes the same commands
 * discoverable to someone who never thinks to try it. Hiding them behind only
 * one of the two loses half the users.
 */

export interface MenuItem {
  label: string;
  onSelect(): void;
  icon?: ReactNode;
  shortcut?: string;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Draws a rule above this item, to group commands. */
  separatorBefore?: boolean;
}

export function ContextMenu({
  items,
  position,
  onClose,
}: {
  items: readonly MenuItem[];
  position: { x: number; y: number };
  onClose(): void;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const [placed, setPlaced] = useState(position);

  // Nudged back inside the window when opened near an edge, so a menu at the
  // bottom of a long file list is not half off-screen.
  useLayoutEffect(() => {
    const element = panel.current;
    if (!element) return;
    const bounds = element.getBoundingClientRect();
    setPlaced({
      x: Math.min(position.x, window.innerWidth - bounds.width - 8),
      y: Math.min(position.y, window.innerHeight - bounds.height - 8),
    });
  }, [position]);

  useEffect(() => {
    const close = () => onClose();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // Capture, so a click anywhere closes before it activates something else.
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={panel}
      role="menu"
      className="fixed z-[92] min-w-52 rounded-lg border border-border-strong bg-surface py-1 shadow-xl"
      style={{ top: placed.y, left: placed.x }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {items.map((item) => (
        <div key={item.label}>
          {item.separatorBefore ? <div className="my-1 h-px bg-[var(--border)]" /> : null}
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled ?? false}
            onClick={() => {
              onClose();
              item.onSelect();
            }}
            className={cx(
              'flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-body-sm transition-fast',
              item.disabled
                ? 'cursor-not-allowed text-subtle'
                : item.tone === 'danger'
                  ? 'text-[var(--danger)] hover:bg-danger-bg'
                  : 'text-body hover:bg-sunken',
            )}
          >
            <span className="flex w-4 shrink-0 justify-center text-muted" aria-hidden>
              {item.icon}
            </span>
            <span className="flex-1">{item.label}</span>
            {item.shortcut ? (
              <span className="font-mono text-micro text-subtle">{item.shortcut}</span>
            ) : null}
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

/**
 * State for one context menu, shared by the row's right-click handler and its
 * kebab button so both open the same thing in the same place.
 */
export function useContextMenu() {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  return {
    position,
    close: () => setPosition(null),
    /** For onContextMenu. */
    openAtPointer(event: React.MouseEvent) {
      event.preventDefault();
      event.stopPropagation();
      setPosition({ x: event.clientX, y: event.clientY });
    },
    /** For a kebab button — anchored under it rather than at the pointer. */
    openUnder(event: React.MouseEvent) {
      event.stopPropagation();
      const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();
      setPosition({ x: bounds.right - 208, y: bounds.bottom + 4 });
    },
  };
}
